import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useSidebarStore } from "./sidebar-store";
import {
  globalRailItems,
  canSeeSettings,
  type DomainId,
} from "@/modules/navigation";
import { hasCapability } from "@brewbase/lib";
import { useCurrentOperator } from "@/hooks/use-operator";
import { useGlobalShortcuts } from "@/components/global-keyboard-shortcuts";
import BrewbaseIcon from "@/components/icons/brewbase-icon";
import { WhatsNewHighlight } from "@/features/announcements/whats-new-highlight";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const RAIL_WIDTH = 44;
export const RAIL_WIDTH_EXPANDED = 152;

// The rail's widths are px while Tailwind's spacing scale is rem on a 14px
// root, so the collapsed geometry is derived here instead of mixing the two:
// a px-sized icon slot filling the padded width is what puts the icons on the
// rail's centre line, in both states.
const RAIL_PADDING = 8;
const RAIL_ICON_SLOT = RAIL_WIDTH - RAIL_PADDING * 2;

interface GlobalRailProps {
  /** Currently active domain for highlighting */
  currentDomain: DomainId | null;
}

/**
 * Global rail - icon-only navigation bar always visible in browse mode.
 * Shows domain icons with tooltips, dividers for grouping.
 */
export function GlobalRail({ currentDomain }: GlobalRailProps) {
  const { openSearch } = useGlobalShortcuts();
  const {
    focus,
    overlayOpen,
    setOverlayOpen,
    openOverlayOnHover,
    scheduleOverlayClose,
    cancelOverlayClose,
    railExpanded,
    setRailExpanded,
    railPeek,
    openRailPeek,
    scheduleRailPeekClose,
    cancelRailPeekClose,
  } = useSidebarStore();
  const { data: operator } = useCurrentOperator();
  const capabilities = operator?.capabilities ?? [];

  // Home has no secondary sidebar to make room for, so the rail stays open
  // there and takes layout width like an ordinary sidebar.
  const isHome = currentDomain === "home" && !focus;
  // Focus mode exists to give the view the width, so it wins over both.
  const pinned = (isHome || railExpanded) && !focus;
  const expanded = pinned || (railPeek && !focus);
  // The latch opens whichever panel this mode keeps hidden: the rail's labels
  // normally, the secondary sidebar under focus mode.
  const latchOpen = focus ? overlayOpen : pinned;

  const visibleItems = globalRailItems.filter((item) => {
    if (item.type === "divider" || !item.capability) return true;
    if (item.capability === "settings") return canSeeSettings(capabilities);
    return hasCapability(capabilities, item.capability);
  });

  return (
    // Off home the outer element holds the collapsed width, so the panel floats
    // over the content whether it is peeked or pinned. Taking layout width
    // instead would reflow and re-centre the page on every toggle.
    <div
      className="relative h-svh shrink-0"
      style={{ width: isHome ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH }}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 flex flex-col bg-olive-3 transition-[width] duration-150 ease-out",
          expanded && !isHome && "z-50 shadow-lg",
        )}
        style={{ width: expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH }}
        onMouseEnter={focus ? cancelOverlayClose : cancelRailPeekClose}
        onMouseLeave={focus ? scheduleOverlayClose : scheduleRailPeekClose}
      >
        {/* Drawn over the panel rather than as a border: a border would eat a
            pixel of the content box and shift every row off centre. */}
        <div className="absolute inset-y-0 right-0 w-px bg-olive-6" />

        <div
          className="flex flex-col py-2 gap-1 border-b border-olive-6"
          style={{ paddingInline: RAIL_PADDING }}
        >
          <RailRow
            expanded={expanded}
            label="Home"
            isActive={currentDomain === "home"}
            href="/"
          >
            <BrewbaseIcon className="w-4 h-4 shrink-0" />
          </RailRow>

          <RailRow
            expanded={expanded}
            label="Search"
            hint="⌘K"
            onClick={openSearch}
          >
            <Search className="w-4 h-4 shrink-0" />
          </RailRow>
        </div>

        {/* Rail items */}
        <div
          className="flex-1 flex flex-col py-2 gap-0.5 overflow-y-auto"
          style={{ paddingInline: RAIL_PADDING }}
        >
          {visibleItems.map((item, index) =>
            item.type === "divider" ? (
              <RailDivider key={`divider-${index}`} />
            ) : (
              <RailRow
                key={item.id}
                expanded={expanded}
                label={item.label}
                badge={item.badge}
                isActive={currentDomain === item.id}
                href={item.href}
              >
                <item.icon className="w-4 h-4 shrink-0" />
              </RailRow>
            ),
          )}
        </div>

        {isHome && (
          <div className="py-2" style={{ paddingInline: RAIL_PADDING }}>
            <WhatsNewHighlight />
          </div>
        )}

        {/*
          No mouseleave: the latch moves with the panel edge as the panel
          widens, so closing on leaving the latch would reopen and reclose
          forever. The panel decides when to close.
        */}
        {!isHome && !(focus && overlayOpen) && (
          <button
            onClick={
              focus
                ? () => setOverlayOpen(!overlayOpen)
                : () => setRailExpanded(!pinned)
            }
            onMouseEnter={focus ? openOverlayOnHover : openRailPeek}
            aria-label={latchOpen ? "Collapse navigation" : "Expand navigation"}
            aria-expanded={latchOpen}
            className="group absolute left-full top-1/2 z-50 flex h-14 w-6 -translate-y-1/2 items-center justify-start"
          >
            {/* -ml-px covers the rail's own right border, so the tab reads as
                the edge stepping out rather than as something stuck to it. */}
            <span
              className={cn(
                "-ml-px flex h-10 w-4 items-center justify-center",
                "border border-l-0 border-olive-6 bg-olive-3",
                "text-muted-foreground transition-colors",
                "group-hover:bg-olive-5 group-hover:text-foreground",
              )}
            >
              {latchOpen ? (
                <ChevronLeft className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

interface RailRowProps {
  expanded: boolean;
  label: string;
  hint?: string;
  badge?: string;
  isActive?: boolean;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
  children: ReactNode;
}

/**
 * The label stays mounted and shrinks to nothing, so the panel's width
 * animation clips it away and back rather than it popping in and out. Expanded,
 * the tooltip would just repeat the visible text, so it is dropped.
 */
function RailRow({
  expanded,
  label,
  hint,
  badge,
  isActive,
  href,
  onClick,
  ariaLabel,
  children,
}: RailRowProps) {
  const className = cn(
    "relative flex items-center h-8 w-full rounded transition-colors duration-150 ease-out",
    expanded && "pr-2",
    isActive
      ? "bg-primary/15 text-primary"
      : "text-muted-foreground hover:text-foreground hover:bg-olive-5",
  );

  const body = (
    <>
      <span
        className="flex shrink-0 items-center justify-center"
        style={{ width: RAIL_ICON_SLOT }}
      >
        {children}
      </span>
      <span className="text-sm truncate min-w-0">
        {label}
        {hint && <span className="ml-1 opacity-60">{hint}</span>}
      </span>
      {badge &&
        (expanded ? (
          <span className="ml-auto text-xs opacity-70">{badge}</span>
        ) : (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
        ))}
    </>
  );

  const control = href ? (
    <Link to={href} className={className} aria-label={ariaLabel}>
      {body}
    </Link>
  ) : (
    <button
      onClick={onClick}
      className={className}
      aria-label={ariaLabel ?? label}
    >
      {body}
    </button>
  );

  if (expanded) return control;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{control}</TooltipTrigger>
      <TooltipContent side="right">
        {label}
        {hint && <span className="ml-1.5 text-xs opacity-70">{hint}</span>}
        {badge && <span className="ml-1.5 text-xs opacity-70">({badge})</span>}
      </TooltipContent>
    </Tooltip>
  );
}

function RailDivider() {
  return (
    <div className="w-full py-1.5">
      <div className="border-t border-olive-6" />
    </div>
  );
}
